import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { DragDropList } from '@/components/ui/custom/dataInput/DragDrop';
import InfinityList from '@/components/ui/custom/InfinityList';
import { Alert, Loading } from '@/components/ui/daisy';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { useConfirm } from '@/data/context/ConfirmModalContext';
import useExamAreas from '@/data/hook/exam/useExamAreas';
import useInfiniteService from '@/data/hook/useInfiniteService';
import useOptimisticReorder from '@/data/hook/useOptimisticReorder';
import { subcategoryAreaQueries } from '@/data/query-keys/subcategoryAreaQueryKey';
import { CourseLineSubCategory } from '@/models/CourseLine';
import ExamArea from '@/models/ExamArea';
import SubcategoryArea from '@/models/SubcategoryArea';

import Fieldset from '../ui/daisy/fieldset';
import ExamAreaCard from './ExamAreaCard';
import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';

type ExamSettingsAreasSectionProps = {
  examId: number;
  subcategory?: CourseLineSubCategory;
};

export default function ExamSettingsAreasSection({
  examId,
  subcategory,
}: ExamSettingsAreasSectionProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.examWorkspace.settings',
  });

  const confirm = useConfirm();

  const {
    examAreas,
    isLoading: isLoadingExamAreas,
    attachArea,
    attachingAreaId,
    detachArea,
    detachingExamAreaId,
    reorderAreas,
    isReordering,
  } = useExamAreas({ examId });

  const {
    results: subcategoryAreas,
    isInitialLoading: isLoadingSubcategoryAreas,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteService<SubcategoryArea>({
    ...subcategoryAreaQueries.listInfinite({
      subcategory,
    }),
    enabled: !!subcategory,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const { optimistic: optimisticExamAreas, handleDragEnd } =
    useOptimisticReorder({
      items: examAreas,
      isReordering,
      onReorder: reorderAreas,
    });

  const attachedAreaIds = useMemo(
    () => new Set(examAreas.map(ea => ea.area.id)),
    [examAreas],
  );

  const unattachedAreas = useMemo(
    () => subcategoryAreas.filter(sa => !attachedAreaIds.has(sa.id)),
    [subcategoryAreas, attachedAreaIds],
  );

  const isLoading = isLoadingExamAreas || isLoadingSubcategoryAreas;
  const noAreas = subcategoryAreas.length === 0 && examAreas.length === 0;

  const handleDetach = async (examArea: ExamArea) => {
    const count = examArea.activities.length;
    if (count === 0) {
      await detachArea(examArea.id);
      return;
    }
    confirm({
      title: t('detachConfirmTitle'),
      description: t('detachConfirmDescription', { count }),
      confirmLabel: t('detachConfirmLabel'),
      variant: 'destructive',
      onConfirm: () => detachArea(examArea.id),
    });
  };

  const { isLimitedEdition } = usePedWorkspace();

  return (
    <Fieldset label={t('areasTitle')} className="flex flex-col gap-4">
      <ConditionalRenderer condition={isLoading}>
        <div className="flex items-center justify-center py-8">
          <Loading variant="spinner" color="primary" />
        </div>
      </ConditionalRenderer>

      <ConditionalRenderer condition={!isLoading && noAreas}>
        <Alert className="rounded-2xl" variant="soft">
          <InformationCircleIcon className="w-6 h-6 shrink-0 text-info" />
          <span className="text-xs"> {t('noAreasFallback')}</span>
        </Alert>
      </ConditionalRenderer>

      <ConditionalRenderer condition={!isLoading && !noAreas}>
        <Alert className="rounded-2xl" variant="soft">
          <InformationCircleIcon className="w-6 h-6 shrink-0 text-info" />
          <span className="text-xs">{t('areasInfo')}</span>
        </Alert>

        <DragDropList
          groupId={`exam-areas-${examId}`}
          onDragEnd={handleDragEnd}
          disabled={
            isReordering ||
            !!detachingExamAreaId ||
            !!attachingAreaId ||
            isLimitedEdition
          }
          className="gap-0"
        >
          {optimisticExamAreas.map((examArea, index) => (
            <ExamAreaCard
              key={examArea.id}
              id={String(examArea.id)}
              index={index}
              isDraggable
              ordinal={index + 1}
              name={examArea.area.name}
              isAttached
              isPending={detachingExamAreaId === examArea.id}
              onToggle={() => handleDetach(examArea)}
              className="mb-4"
            />
          ))}
        </DragDropList>
        <InfinityList
          className="flex flex-col gap-0 -mt-4"
          onReachEnd={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
        >
          {unattachedAreas.map(area => (
            <ExamAreaCard
              key={area.id}
              name={area.name}
              isPending={attachingAreaId === area.id}
              onToggle={() => attachArea(area.id)}
              className="mb-4"
            />
          ))}
        </InfinityList>
      </ConditionalRenderer>
    </Fieldset>
  );
}
